import { useMutation, useQuery, useQueryClient, type UseMutationResult, type UseQueryResult } from '@tanstack/react-query';

import {
  fetchAiStaffSettings,
  type AiStaffSettings,
  type UpdateAiStaffSettingsPayload,
  updateAiStaffSettings,
} from '@/lib/api/aiStaff';
import {
  fetchAiStaffTickets,
  fetchAiStaffActions,
  recordAiStaffAction,
  createAiStaffTicket,
  updateAiStaffSessionStatus,
  fetchAiStaffUsageSummary,
  type AiStaffTicket,
  type AiAssistantAction,
  type CreateAiStaffTicketPayload,
  type UpdateAiStaffSessionStatusPayload,
  type RecordAiStaffActionPayload,
  type AiStaffTicketListResponse,
  type AiAssistantActionListResponse,
  type AiUsageSummary,
} from '@/lib/api/aiStaffAutomation';

export const useAiStaffTickets = (
  params: { status?: AiStaffTicket['status']; assignedStaffId?: string | null; limit?: number } = {},
): UseQueryResult<AiStaffTicketListResponse> => {
  return useQuery({
    queryKey: ['ai-staff', 'tickets', params.status ?? 'all', params.assignedStaffId ?? 'all', params.limit ?? 20],
    queryFn: ({ signal }) =>
      fetchAiStaffTickets({
        status: params.status,
        assignedStaffId: params.assignedStaffId === null ? 'unassigned' : params.assignedStaffId,
        limit: params.limit,
        signal,
      }),
    staleTime: 15_000,
  });
};

export const useAiStaffActions = (
  params: { sessionId?: string; ticketId?: string; limit?: number } = {},
): UseQueryResult<AiAssistantActionListResponse> => {
  return useQuery({
    queryKey: [
      'ai-staff',
      'actions',
      params.sessionId ?? 'all',
      params.ticketId ?? 'all',
      params.limit ?? 30,
    ],
    queryFn: ({ signal }) =>
      fetchAiStaffActions({
        sessionId: params.sessionId,
        ticketId: params.ticketId,
        limit: params.limit,
        signal,
      }),
    enabled: Boolean(params.sessionId || params.ticketId),
    staleTime: 10_000,
  });
};

export const useCreateAiStaffTicket = (): UseMutationResult<
  AiStaffTicket,
  unknown,
  CreateAiStaffTicketPayload
> => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (payload) => createAiStaffTicket(payload),
    onSuccess: (ticket) => {
      void queryClient.invalidateQueries({ queryKey: ['ai-staff', 'tickets'] });
      if (ticket.sessionId) {
        void queryClient.invalidateQueries({
          queryKey: ['ai-staff', 'sessions'],
        });
      }
    },
  });
};

export const useUpdateAiStaffSessionStatus = (): UseMutationResult<
  { session: { id: string }; ticket: AiStaffTicket | null },
  unknown,
  UpdateAiStaffSessionStatusPayload
> => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (payload) => updateAiStaffSessionStatus(payload),
    onSuccess: (data) => {
      void queryClient.invalidateQueries({ queryKey: ['ai-staff', 'sessions'] });
      if (data.ticket) {
        void queryClient.invalidateQueries({ queryKey: ['ai-staff', 'tickets'] });
      }
    },
  });
};

export const useCreateAiStaffAction = (): UseMutationResult<
  { action: AiAssistantAction; ticket: AiStaffTicket | null },
  unknown,
  RecordAiStaffActionPayload
> => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (payload) => recordAiStaffAction(payload),
    onSuccess: (data, variables) => {
      void queryClient.invalidateQueries({ queryKey: ['ai-staff', 'actions', variables.sessionId] });
      if (variables.ticketId) {
        void queryClient.invalidateQueries({ queryKey: ['ai-staff', 'actions', variables.ticketId] });
        void queryClient.invalidateQueries({ queryKey: ['ai-staff', 'tickets'] });
      }
    },
  });
};

export const useAiStaffUsageSummary = (): UseQueryResult<AiUsageSummary> => {
  return useQuery({
    queryKey: ['ai-staff', 'usage', 'summary'],
    staleTime: 60_000,
    queryFn: ({ signal }) => fetchAiStaffUsageSummary({ signal }),
  });
};

export const useAiStaffSettings = (): UseQueryResult<AiStaffSettings> => {
  return useQuery({
    queryKey: ['ai-staff', 'settings'],
    staleTime: 30_000,
    queryFn: ({ signal }) => fetchAiStaffSettings({ signal }),
  });
};

export const useUpdateAiStaffSettings = (): UseMutationResult<
  AiStaffSettings,
  unknown,
  UpdateAiStaffSettingsPayload
> => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (payload) => updateAiStaffSettings(payload),
    onSuccess: (data) => {
      queryClient.setQueryData(['ai-staff', 'settings'], data);
    },
  });
};
